import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyL,
  Caption,
  DisplayL,
  HeadlineL,
} from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Pictures } from 'components/landing/EarlyAccess/types';

import cashAvif from './assets/cash.avif';
import cash from './assets/cash.png';
import jerseyAvif from './assets/jersey.avif';
import jersey from './assets/jersey.png';
import ticketsAvif from './assets/tickets.avif';
import tickets from './assets/tickets.png';
import vipAvif from './assets/vip.avif';
import vip from './assets/vip.png';
import winAvif from './assets/win.avif';
import win from './assets/win.jpg';

const Root = styled(ButtonBase)`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  isolation: isolate;
  text-align: left;
  padding: var(--double-unit);
  border-radius: 40px;
  background-image: url(${win}});
  background-image: image-set(
    url(${winAvif}) type('image/avif'),
    url(${win}}) type('image/jpeg')
  );
  background-size: cover;
  width: 100%;
  @media ${tabletAndAbove} {
    padding: 15% 10%;
  }
`;
const Content = styled(Vertical)`
  margin: var(--double-unit) auto;
  @media ${tabletAndAbove} {
    align-items: flex-start;
    max-width: 360px;
  }
`;

const RewardsImage = styled.div`
  --blockHeight: auto;
  display: grid;
  max-width: 320px;
  margin: -20% auto 0;
  column-gap: var(--quadruple-unit);
  grid-template:
    'vip cards' var(--blockHeight) 'cash cash' min-content 'jersey tickets' var(
      --blockHeight
    )
    / 1fr 1fr;
  align-items: center;
  justify-items: center;
  picture {
    display: block;
    max-width: 100%;
    height: var(--blockHeight);
  }
  @media ${tabletAndAbove} {
    --blockHeight: 260px;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(0, -50%);
    margin: 0;
    max-width: calc(90% - 360px);
  }
`;
const Disclaimer = styled(Caption)`
  @media ${tabletAndAbove} {
    position: absolute;
    inset: auto 10% 10%;
    max-width: 360px;
  }
`;
const Label = styled(HeadlineL)`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`;
const Vip = styled.div`
  position: relative;
  grid-area: vip;
  margin-left: auto;
`;
const Cards = styled.div`
  position: relative;
  grid-area: cards;
  margin-right: auto;
`;
const Cash = styled.div`
  position: relative;
  grid-area: cash;
  picture {
    display: block;
    height: auto;
  }
`;
const Jersey = styled.div`
  position: relative;
  grid-area: jersey;
  margin-left: auto;
`;
const Tickets = styled.div`
  position: relative;
  grid-area: tickets;
  margin-right: auto;
`;

export type WinAssets = {
  cards: Pictures;
};
type Props = {
  assets: WinAssets;
};
export const Win = ({ assets }: Props) => {
  const { signUp } = useConnectionContext();
  return (
    <Root onClick={signUp}>
      <div>
        <Content gap={5}>
          <Vertical gap={3}>
            <DisplayL>
              <FormattedMessage
                id="landing.EarlyAccess.Win.title"
                defaultMessage="Win"
              />
            </DisplayL>
            <BodyL>
              <FormattedMessage
                id="landing.EarlyAccess.Win.description"
                defaultMessage="Play for more than bragging rights. Compete for cash, cards, signed jerseys, VIP experiences and much more."
              />
            </BodyL>
          </Vertical>
          <Button asSpan color="secondary" size="large">
            <FormattedMessage
              id="landing.EarlyAccess.Win.cta"
              defaultMessage="Compete now"
            />
          </Button>
        </Content>
        <Disclaimer color="var(--c-neutral-500)" uppercase>
          <FormattedMessage
            id="landing.EarlyAccess.Win.disclaimer"
            defaultMessage="*Approximate value of the cash rewards over the season. Actual rewards and value may vary and may be awarded in cash or in ETH"
          />
        </Disclaimer>
      </div>
      <RewardsImage>
        <Vip className="text-center">
          <picture>
            <source srcSet={vipAvif} type="image/avif" />
            <Img src={vip} fetchpriority="low" width="197" height="248" />
          </picture>
          <Label brand>
            <FormattedMessage
              id="landing.EarlyAccess.Win.vip"
              defaultMessage="Vip Experience"
            />
          </Label>
        </Vip>
        <Cards className="text-center">
          <picture>
            <source srcSet={assets.cards.avif} type="image/avif" />
            <Img
              src={assets.cards.pictureUrl}
              fetchpriority="low"
              width="210"
              height="193"
            />
          </picture>
          <Label brand>
            <FormattedMessage
              id="landing.EarlyAccess.Win.cards"
              defaultMessage="Playable Cards"
            />
          </Label>
        </Cards>

        <Cash>
          <picture>
            <source srcSet={cashAvif} type="image/avif" />
            <Img src={cash} fetchpriority="low" width="554" height="198" />
          </picture>
        </Cash>
        <Jersey className="text-center">
          <picture>
            <source srcSet={jerseyAvif} type="image/avif" />
            <Img src={jersey} fetchpriority="low" width="161" height="161" />
          </picture>
          <Label brand>
            <FormattedMessage
              id="landing.EarlyAccess.Win.jersey"
              defaultMessage="Signed Jersey"
            />
          </Label>
        </Jersey>
        <Tickets className="text-center">
          <picture>
            <source srcSet={ticketsAvif} type="image/avif" />
            <Img src={tickets} fetchpriority="low" width="136" height="168" />
          </picture>
          <Label brand>
            <FormattedMessage
              id="landing.EarlyAccess.Win.tickets"
              defaultMessage="Match Tickets"
            />
          </Label>
        </Tickets>
      </RewardsImage>
    </Root>
  );
};
